import { getBackendUrl } from "../lib/utils";
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, ArrowLeft, Share2, Images, X, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { getImageUrl } from '../lib/utils';

const fallbackNews = [
  {
    id: 1,
    title: 'S.S. İkitelli-Silivri Toplu İşyeri Kooperatifi Temel Atma Töreni',
    date: '12 Ağustos 2024',
    image: '/images/renders/render1.jpg',
    summary: 'Silivri\'de yükselecek olan yeni iş merkezimizin temel atma törenini büyük bir coşku ile gerçekleştirdik. Projemizin ilk aşaması başarıyla başladı.',
    content: "Silivri'nin en değerli ticari arazilerinden birinde konumlanan projemizin ilk kazması vuruldu. Törene bölge protokolü, kooperatif yönetimimiz ve birçok değerli üyemiz katılım gösterdi. Konuşmalarda projenin bölge ekonomisine katacağı değere ve modern iş alanlarının önemine vurgu yapıldı. \n\nTemel atma süreci planlanandan önce tamamlanmış olup, inşaat takvimi kesintisiz şekilde devam etmektedir."
  },
  {
    id: 2,
    title: 'Yeni Yönetim Kurulu Toplantısı Gerçekleştirildi',
    date: '05 Eylül 2024',
    image: '/images/renders/render2.jpg',
    summary: 'Proje ilerlemesinin değerlendirildiği aylık yönetim kurulu toplantımızda önemli kararlar alındı ve yatırım planları tartışıldı.',
    content: "Kooperatif merkezimizde gerçekleştirilen yönetim kurulu toplantısında, inşaatın ilerleme durumu detaylı olarak incelendi. Maliyet analizleri ve tedarik zinciri raporları üyelerimiz lehine optimize edilecek şekilde karara bağlandı. Alınan kararlar neticesinde sosyal donatı alanlarının genişletilmesi ve akıllı altyapı sistemlerine ek bütçe ayrılması kararlaştırıldı."
  },
  {
    id: 3,
    title: 'Altyapı Çalışmalarında 1. Etap Tamamlandı',
    date: '20 Ekim 2024',
    image: '/images/renders/render3.jpg',
    summary: 'Kooperatif arazi alanında başlatılan kazı, su, elektrik ve telekomünikasyon altyapı çalışmaları planlanan tarihten önce bitirildi.',
    content: "Sanayi ve ticaret alanlarının en önemli gereksinimi olan kesintisiz ve güçlü altyapı sistemleri için başlattığımız 1. etap çalışmaları tamamlandı. Yeni nesil fiber optik ağlar, yüksek kapasiteli elektrik trafoları ve dayanıklı su şebekeleri bölgeye başarıyla entegre edildi. 2. etap üstyapı çalışmaları için zemin hazır hale getirildi."
  }
];

const galleryImages = [
  '/images/renders/render1.jpg',
  '/images/renders/render2.jpg',
  '/images/renders/render3.jpg',
  '/images/renders/render4.jpg',
  '/images/renders/render5.jpg',
];

const NewsDetail = () => {
  const { id } = useParams();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const navigate = useNavigate();

  const newsGallery = (() => {
    if (!news) return [];
    let list = [];
    try {
      if (news.gallery) {
        list = typeof news.gallery === 'string' ? JSON.parse(news.gallery) : news.gallery;
      }
    } catch (e) {
      list = [];
    }
    return Array.isArray(list) ? list : [];
  })();

  const openLightbox = (i) => setLightboxIndex(i);
  const closeLightbox = () => setLightboxIndex(null);
  const prevImage = (e) => {
    e?.stopPropagation();
    setLightboxIndex((i) => (i - 1 + newsGallery.length) % newsGallery.length);
  };
  const nextImage = (e) => {
    e?.stopPropagation();
    setLightboxIndex((i) => (i + 1) % newsGallery.length);
  };

  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'ArrowRight') nextImage();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIndex, newsGallery.length]);

  useEffect(() => {
    const fetchNewsDetail = async () => {
      try {
        // Fallback for short mock IDs
        if (id && id.length < 5) {
          const mock = fallbackNews.find(n => String(n.id || n._id) === id);
          if (mock) { setNews(mock); setLoading(false); return; }
        }

        const backendUrl = getBackendUrl();
        const { data } = await axios.get(`${backendUrl}/api/news/${id}`);
        setNews(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchNewsDetail();
  }, [id]);

  useEffect(() => {
    if (!news) return;

    const previousTitle = document.title;
    
    // Fallback logic for title
    const seoTitle = news.seo_title && news.seo_title.trim() !== '' 
      ? news.seo_title 
      : news.title;
    document.title = `${seoTitle} | İSİLKOOP`;

    // Fallback logic for description (truncate to 160 chars)
    const rawDesc = news.seo_description && news.seo_description.trim() !== '' 
      ? news.seo_description 
      : (news.summary || '');
    const seoDesc = rawDesc.length > 160 ? rawDesc.substring(0, 160) + '...' : rawDesc;

    const metaDescription = document.querySelector('meta[name="description"]');
    let previousDescription = '';
    if (metaDescription) {
      previousDescription = metaDescription.getAttribute('content') || '';
      metaDescription.setAttribute('content', seoDesc);
    }

    // Dynamic OpenGraph / Twitter Card Meta Tag Injection
    const ogTags = {
      'og:title': seoTitle,
      'og:description': seoDesc,
      'og:image': news.image ? getImageUrl(news.image) : '',
      'og:url': window.location.href,
      'og:type': 'article',
      'twitter:card': 'summary_large_image',
      'twitter:title': seoTitle,
      'twitter:description': seoDesc,
      'twitter:image': news.image ? getImageUrl(news.image) : '',
    };

    const createdMetaTags = [];
    Object.entries(ogTags).forEach(([key, value]) => {
      if (!value) return;
      const isOg = key.startsWith('og:');
      const attr = isOg ? 'property' : 'name';
      
      // Update existing tag or create new
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (el) {
        const prevVal = el.getAttribute('content');
        el.setAttribute('content', value);
        createdMetaTags.push({ el, attr, key, prevVal, isNew: false });
      } else {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        el.setAttribute('content', value);
        document.head.appendChild(el);
        createdMetaTags.push({ el, attr, key, prevVal: null, isNew: true });
      }
    });

    // Dynamic JSON-LD Schema (NewsArticle markup)
    const schemaData = {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      "headline": news.title,
      "description": news.summary || seoDesc,
      "image": news.image ? [getImageUrl(news.image)] : [],
      "datePublished": news.date_created || new Date().toISOString(),
      "author": {
        "@type": "Organization",
        "name": "İSİLKOOP",
        "url": window.location.origin
      },
      "publisher": {
        "@type": "Organization",
        "name": "İSİLKOOP",
        "logo": {
          "@type": "ImageObject",
          "url": `${window.location.origin}/logo.png`
        }
      }
    };

    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.text = JSON.stringify(schemaData);
    schemaScript.id = 'news-schema-jsonld';
    document.head.appendChild(schemaScript);

    return () => {
      document.title = previousTitle;
      if (metaDescription) {
        metaDescription.setAttribute('content', previousDescription);
      }
      
      // Cleanup custom OpenGraph tags
      createdMetaTags.forEach(({ el, attr, key, prevVal, isNew }) => {
        if (isNew) {
          el.remove();
        } else {
          el.setAttribute('content', prevVal || '');
        }
      });

      // Cleanup Schema script
      const scriptEl = document.getElementById('news-schema-jsonld');
      if (scriptEl) scriptEl.remove();
    };
  }, [news]);

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex justify-center bg-slate-50">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mt-20"></div>
      </div>
    );
  }

  if (!news) {
    return (
      <div className="min-h-screen pt-32 flex flex-col items-center justify-center bg-slate-50">
        <h2 className="text-2xl font-bold text-slate-800 mb-4">Haber bulunamadı.</h2>
        <button onClick={() => navigate('/haberler')} className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium">Geri Dön</button>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24 min-h-screen bg-slate-50">
      <div className="container mx-auto px-6 max-w-4xl">
        <Link to="/haberler" className="inline-flex items-center gap-2 text-slate-500 hover:text-blue-600 transition-colors mb-8 font-medium">
          <ArrowLeft size={20} /> Haberlere Dön
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
          {news.image && (
            <div className="w-full h-[400px] md:h-[500px] relative bg-slate-100">
              <img 
                src={getImageUrl(news.image)} 
                alt={news.title} 
                className="w-full h-full object-cover" 
              />
            </div>
          )}
          <div className="p-8 md:p-12">
            <div className="flex items-center gap-4 text-sm font-semibold text-slate-500 mb-6">
              <span className="flex items-center gap-2 text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg">
                <Calendar size={16} /> {news.date}
              </span>
              <button onClick={() => navigator.clipboard.writeText(window.location.href).then(() => alert('Bağlantı kopyalandı!'))} className="flex items-center gap-2 cursor-pointer hover:text-slate-800 transition-colors">
                <Share2 size={16} /> Paylaş
              </button>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 leading-tight">
              {news.title}
            </h1>
            
            <div className="prose prose-lg prose-slate max-w-none">
              <p className="text-xl text-slate-600 font-medium mb-8 leading-relaxed">
                {news.summary}
              </p>
              
              {news.content && (
                <div 
                  className="text-slate-600 leading-relaxed whitespace-pre-wrap" 
                  dangerouslySetInnerHTML={{ __html: news.content }} 
                />
              )}
            </div>
          </div>
        </motion.div>

        {newsGallery.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mt-16"
          >
            <div className="flex items-center gap-3 mb-8">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-blue-50 text-blue-600">
                <Images size={20} />
              </span>
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Galeri</h2>
                <p className="text-sm text-slate-500">Habere ait diğer görseller</p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {newsGallery.map((src, i) => (
                <button
                  key={`${src}-${i}`}
                  onClick={() => openLightbox(i)}
                  className="relative overflow-hidden rounded-2xl bg-slate-100 aspect-[4/3] group focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <img
                    src={getImageUrl(src)}
                    alt={`Galeri görseli ${i + 1}`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/30 transition-colors" />
                </button>
              ))}
            </div>
          </motion.section>
        )}
      </div>

      {lightboxIndex !== null && (
        <div
          onClick={closeLightbox}
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
        >
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
            aria-label="Kapat"
          >
            <X size={24} />
          </button>
          {newsGallery.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
                aria-label="Önceki"
              >
                <ChevronLeft size={28} />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
                aria-label="Sonraki"
              >
                <ChevronRight size={28} />
              </button>
            </>
          )}
          <img
            src={getImageUrl(newsGallery[lightboxIndex])}
            alt={`Galeri görseli ${lightboxIndex + 1}`}
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
          />
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-white/10 text-white text-sm font-medium">
            {lightboxIndex + 1} / {newsGallery.length}
          </div>
        </div>
      )}
    </div>
  );
}

export default NewsDetail;
